import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { AlertCircle, Loader } from 'lucide-react';

/**
 * Meeting Iframe Component
 * Securely embeds MiroTalk meeting in an iframe
 * 
 * - URL is NOT visible to user (no history/sharing)
 * - Token expires after use
 * - Proper role-based access (Admin/Speaker = presenter, User = viewer)
 */
const MeetingIframe = () => {
  const { meetingId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useTheme();
  const { user } = useAuth();

  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mirotalkUrl, setMirotalkUrl] = useState(null);
  const [userRole, setUserRole] = useState('USER');
  const [showExitModal, setShowExitModal] = useState(false);
  const [pendingLeaveAction, setPendingLeaveAction] = useState(null);
  const iframeRef = useRef(null);

  const getBackUrl = useCallback(() => {
    const routes = {
      ADMIN: '/admin/meetings',
      SPEAKER: '/speaker/meetings',
      USER: '/user/meetings',
    };

    return routes[userRole || user?.role] || '/';
  }, [user?.role, userRole]);

  useEffect(() => {
    // Get the URL from location state (passed from SecureMeetingJoin)
    if (location.state?.mirotalkUrl) {
      setMirotalkUrl(location.state.mirotalkUrl);
      setUserRole(location.state.role || 'USER');
      setLoading(false);
    } else {
      setError('Meeting URL not found. Please join the meeting again.');
    }
  }, [location]);

  useEffect(() => {
    const handleMiroTalkMessage = async (event) => {
      if (!mirotalkUrl) {
        return;
      }

      const expectedOrigin = new URL(mirotalkUrl).origin;
      if (event.origin !== expectedOrigin) {
        return;
      }

      const data = event.data;
      if (!data || data.source !== 'mirotalk-sfu' || data.type !== 'meeting-left') {
        return;
      }

      try {
        if (data.action === 'end-for-all' && userRole === 'ADMIN') {
          await api.post(`/meetings/${meetingId}/end`);
        }
      } catch (requestError) {
        console.error('Failed to update meeting status on leave:', requestError);
      } finally {
        setPendingLeaveAction(null);
        navigate(getBackUrl(), { replace: true });
      }
    };

    window.addEventListener('message', handleMiroTalkMessage);

    return () => {
      window.removeEventListener('message', handleMiroTalkMessage);
    };
  }, [getBackUrl, meetingId, mirotalkUrl, navigate, userRole]);

  const postMiroTalkCommand = (type) => {
    if (!iframeRef.current?.contentWindow || !mirotalkUrl) {
      navigate(getBackUrl(), { replace: true });
      return;
    }

    const targetOrigin = new URL(mirotalkUrl).origin;
    setPendingLeaveAction(type);
    iframeRef.current.contentWindow.postMessage(
      {
        source: 'conference-app',
        type,
      },
      targetOrigin
    );

    window.setTimeout(() => {
      setPendingLeaveAction((currentAction) => {
        if (!currentAction) {
          return currentAction;
        }

        navigate(getBackUrl(), { replace: true });
        return null;
      });
    }, 1500);
  };

  const handleLeaveMeeting = () => {
    setShowExitModal(false);
    postMiroTalkCommand('leave-meeting');
  };

  const handleEndForAll = () => {
    setShowExitModal(false);
    postMiroTalkCommand('end-meeting-for-all');
  };

  const handleIframeError = () => {
    setError('Unable to load meeting. Please make sure the MiroTalk server is running and try again.');
  };

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.errorIcon}>
            <AlertCircle size={48} color={theme.colors.danger} />
          </div>
          <h2 style={styles.title}>Error Loading Meeting</h2>
          <p style={styles.errorText}>{error}</p>
          <button
            onClick={() => navigate(-1)}
            style={{ ...styles.button, backgroundColor: theme.colors.primary }}
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (loading || !mirotalkUrl) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <Loader size={48} style={styles.spinner} />
          <p style={styles.loadingText}>Preparing meeting...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.iframeContainer}>
      <div style={styles.headerBar}>
        <button
          onClick={() => setShowExitModal(true)}
          style={styles.leaveButton}
          disabled={Boolean(pendingLeaveAction)}
        >
          {pendingLeaveAction ? 'Leaving...' : userRole === 'ADMIN' ? 'Leave Options' : 'Leave Meeting'}
        </button>
      </div>
      
      {/* 
        MiroTalk Iframe
        - URL is not visible in address bar
        - Token is temporary and one-time use
        - Role-based access enforced on backend AND browser permissions
        - ADMIN/SPEAKER: Full media permissions (camera, microphone, screen share)
        - USER: Minimal permissions (chat, viewing only)
      */}
      <iframe
        ref={iframeRef}
        src={mirotalkUrl}
        title="MiroTalk Meeting"
        style={styles.iframe}
        allow={
          userRole === 'ADMIN' || userRole === 'SPEAKER'
            ? "camera; microphone; display-capture; clipboard-read; clipboard-write; web-share; autoplay; picture-in-picture"
            : "clipboard-read; clipboard-write; web-share; autoplay; picture-in-picture"
        }
        allowFullScreen
        referrerPolicy="no-referrer-when-downgrade"
        onError={handleIframeError}
      />

      {showExitModal && (
        <div style={styles.modalOverlay} onClick={() => setShowExitModal(false)}>
          <div style={styles.modalCard} onClick={(event) => event.stopPropagation()}>
            <h3 style={styles.modalTitle}>Leave Meeting</h3>
            <p style={styles.modalText}>
              {userRole === 'ADMIN'
                ? 'Choose whether to leave the meeting yourself or end it for everyone.'
                : 'Leaving will return you to your meetings dashboard.'}
            </p>
            <div style={styles.modalActions}>
              <button onClick={() => setShowExitModal(false)} style={styles.secondaryButton}>
                Cancel
              </button>
              {userRole === 'ADMIN' && (
                <button onClick={handleEndForAll} style={styles.dangerButton}>
                  End For All
                </button>
              )}
              <button onClick={handleLeaveMeeting} style={styles.primaryButton}>
                Leave Meeting
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    padding: '20px',
  },
  card: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '40px',
    maxWidth: '500px',
    width: '100%',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
    textAlign: 'center',
  },
  errorIcon: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '20px',
  },
  title: {
    fontSize: '24px',
    fontWeight: '600',
    marginBottom: '12px',
    color: '#333',
  },
  errorText: {
    fontSize: '14px',
    color: '#666',
    marginBottom: '24px',
    lineHeight: '1.6',
  },
  button: {
    padding: '12px 24px',
    fontSize: '14px',
    fontWeight: '600',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    color: '#fff',
    transition: 'opacity 0.2s',
  },
  spinner: {
    animation: 'spin 1s linear infinite',
    margin: '0 auto 20px',
  },
  loadingText: {
    fontSize: '16px',
    color: '#333',
    fontWeight: '500',
  },
  iframeContainer: {
    width: '100%',
    height: '100vh',
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: '#111827',
  },
  headerBar: {
    position: 'absolute',
    top: '16px',
    right: '16px',
    zIndex: 1001,
  },
  leaveButton: {
    padding: '10px 16px',
    fontSize: '14px',
    fontWeight: '600',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    color: '#fff',
    backgroundColor: '#dc2626',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.25)',
  },
  iframe: {
    width: '100%',
    height: '100%',
    border: 'none',
    borderRadius: 0,
  },
  sslWarning: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    backgroundColor: '#fff3cd',
    color: '#856404',
    padding: '12px 20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    fontSize: '14px',
    borderBottom: '1px solid #ffc107',
  },
  sslWarningText: {
    display: 'flex',
    alignItems: 'center',
  },
  sslLink: {
    color: '#0066cc',
    textDecoration: 'underline',
    fontWeight: '600',
  },
  modalOverlay: {
    position: 'absolute',
    inset: 0,
    backgroundColor: 'rgba(15, 23, 42, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1002,
    padding: '20px',
  },
  modalCard: {
    width: '100%',
    maxWidth: '420px',
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 24px 60px rgba(15, 23, 42, 0.28)',
  },
  modalTitle: {
    margin: '0 0 8px 0',
    fontSize: '20px',
    fontWeight: '700',
    color: '#111827',
  },
  modalText: {
    margin: '0 0 20px 0',
    fontSize: '14px',
    lineHeight: '1.6',
    color: '#4b5563',
  },
  modalActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px',
  },
  primaryButton: {
    padding: '10px 16px',
    border: 'none',
    borderRadius: '8px',
    backgroundColor: '#2563eb',
    color: '#ffffff',
    cursor: 'pointer',
    fontWeight: '600',
  },
  secondaryButton: {
    padding: '10px 16px',
    borderRadius: '8px',
    border: '1px solid #d1d5db',
    backgroundColor: '#ffffff',
    color: '#111827',
    cursor: 'pointer',
    fontWeight: '600',
  },
  dangerButton: {
    padding: '10px 16px',
    border: 'none',
    borderRadius: '8px',
    backgroundColor: '#dc2626',
    color: '#ffffff',
    cursor: 'pointer',
    fontWeight: '600',
  },
};

export default MeetingIframe;
